import './index.css';
import type { RadialToolbarOptions, RadialMenuItem } from './types';
import type { ThemeConfig } from '../../themes/types';
import { IBimComponent } from '../../types/component';
import { getIcon } from '../../utils/icon-manager';
import { themeManager } from '../../services/theme';
import { localeManager, t } from '../../services/locale';

export class RadialToolbar implements IBimComponent {
    private container: HTMLElement;
    private wrapper: HTMLElement;
    private mainButton: HTMLButtonElement;
    private items: RadialMenuItem[] = [];
    private itemElements: HTMLButtonElement[] = [];
    private itemButtonMap: Map<string, HTMLButtonElement> = new Map();
    private toggleStateMap: Map<string, boolean> = new Map();
    private isActive = false;
    private unsubscribeTheme: (() => void) | null = null;
    private unsubscribeLocale: (() => void) | null = null;
    private readonly mainButtonLabel: string;
    private readonly onMainButtonClick?: () => void;

    // 主按钮直径（px）
    private readonly MAIN_BUTTON_SIZE = 60;
    // 子按钮直径（px）
    private readonly SUB_BUTTON_SIZE = 46;
    // 第一环"子按钮中心"到"主按钮中心"的距离（px）
    private readonly BASE_RADIUS = 80;
    // 多环时，相邻两环的中心半径差（px）
    private readonly RING_GAP = 60;
    // 扇形展开角度范围 (180为正左边/下，270为正上)
    private readonly FAN_START_DEG = 170;
    private readonly FAN_END_DEG = 280;
    // 扇形边缘留白（px），防止按钮贴边或裁切
    private readonly CANVAS_PADDING = 28;

    // 【自定义每环数量】：你可以随意修改这里的数字！多出来的按钮会自动折叠到更外围的新环。
    private readonly FIXED_RING_CAPACITIES = [4, 6, 8];

    constructor(options: RadialToolbarOptions) {
        this.container = options.container;
        this.items = options.items === undefined ? this.createDefaultItems() : [...options.items];
        this.mainButtonLabel = options.mainButtonLabel ?? 'toolbar.home';
        this.onMainButtonClick = options.onMainButtonClick;

        this.wrapper = this.createWrapper();
        this.mainButton = this.createMainButton(options.mainButtonIcon);
        this.wrapper.appendChild(this.mainButton);

        this.container.appendChild(this.wrapper);
        this.renderItems();
        this.updateLayoutMetrics();
        this.bindEvents();

        this.setTheme(themeManager.getTheme());
        this.updateLocales();
        this.unsubscribeTheme = themeManager.subscribe((theme) => {
            this.setTheme(theme);
        });
        this.unsubscribeLocale = localeManager.subscribe(() => {
            this.updateLocales();
        });
    }

    private createDefaultItems(): RadialMenuItem[] {
        return [
            { id: 'zoom', label: 'toolbar.zoomBox', icon: getIcon('框选放大') },
            { id: 'measure', label: 'toolbar.measure', icon: getIcon('测量') },
            { id: 'section', label: 'toolbar.section', icon: getIcon('剖切') },
            { id: 'walk', label: 'toolbar.walk', icon: getIcon('漫游') },
            { id: 'setting', label: 'toolbar.setting', icon: getIcon('设置') }
        ];
    }

    private createWrapper(): HTMLElement {
        const el = document.createElement('div');
        el.className = 'radial-toolbar-wrapper';
        return el;
    }

    private createMainButton(icon?: string): HTMLButtonElement {
        const btn = document.createElement('button');
        btn.className = 'radial-main-btn';
        btn.type = 'button';
        btn.innerHTML = icon ?? getIcon('主视角');
        return btn;
    }

    private bindEvents(): void {
        this.mainButton.addEventListener('click', this.handleMainButtonClick);
        this.mainButton.addEventListener('dblclick', this.handleMainButtonDoubleClick);
    }

    private readonly handleMainButtonClick = (event: MouseEvent): void => {
        event.stopPropagation();

        if (this.isActive) {
            this.collapse();
        } else {
            this.expand();
        }
    };

    private readonly handleMainButtonDoubleClick = (event: MouseEvent): void => {
        event.stopPropagation();
        if (this.onMainButtonClick) {
            this.onMainButtonClick();
        }
    };

    private renderItems(): void {
        this.itemElements.forEach((el) => el.remove());
        this.itemElements = [];
        this.itemButtonMap.clear();
        this.toggleStateMap.clear();

        this.items.forEach((item, index) => {
            this.toggleStateMap.set(item.id, Boolean(item.isActive));
            const btn = this.createSubButton(item, index);
            this.wrapper.insertBefore(btn, this.mainButton);
            this.itemElements.push(btn);
            this.itemButtonMap.set(item.id, btn);
        });

        this.updateItemPositions();
    }

    private createSubButton(item: RadialMenuItem, index: number): HTMLButtonElement {
        const btn = document.createElement('button');
        btn.className = 'radial-sub-btn';
        btn.type = 'button';
        btn.dataset.index = String(index);
        this.applyItemActiveClass(btn, item);

        const iconContainer = document.createElement('span');
        iconContainer.className = 'radial-sub-btn-icon';
        if (item.icon) {
            iconContainer.innerHTML = item.icon;
        } else {
            iconContainer.textContent = this.getFallbackLabel(item.label);
        }

        btn.appendChild(iconContainer);
        btn.addEventListener('click', (event) => {
            event.stopPropagation();
            if (item.isToggle) {
                const current = this.toggleStateMap.get(item.id) ?? false;
                const next = !current;
                this.toggleStateMap.set(item.id, next);
                item.isActive = next;
                this.applyItemActiveClass(btn, item);
                item.onToggle?.(next, item);
                return;
            }
            item.onClick?.(item);
        });

        return btn;
    }

    /**
     * 根据预设的数组计算容量（不足自动向后排）
     */
    private getRingCapacities(total: number): number[] {
        const capacities: number[] = [];
        let remaining = total;

        for (const cap of this.FIXED_RING_CAPACITIES) {
            if (remaining <= 0) break;
            const actualCount = Math.min(cap, remaining);
            capacities.push(actualCount);
            remaining -= actualCount;
        }

        // 如果配置的环数不够用，剩下的全部折叠到最后一环
        if (remaining > 0) {
            capacities.push(remaining);
        }

        return capacities;
    }

    private updateItemPositions(): void {
        const total = this.itemElements.length;
        if (total === 0) return;

        const fanSpan = this.FAN_END_DEG - this.FAN_START_DEG;
        const ringCapacities = this.getRingCapacities(total);

        let globalIndex = 0;
        ringCapacities.forEach((ringCount, ringIndex) => {
            const radius = this.BASE_RADIUS + ringIndex * this.RING_GAP;

            for (let ringLocalIndex = 0; ringLocalIndex < ringCount; ringLocalIndex++) {
                const btn = this.itemElements[globalIndex];

                // 【核心逻辑】：首尾固定：第一个按钮在最下面(180)，最后一个在最上面(270)，中间按剩余空间均分
                // 如果该环只有1个按钮，让它待在180度的起始位置
                const ratio = ringCount <= 1 ? 0 : ringLocalIndex / (ringCount - 1);

                const angleDeg = this.FAN_START_DEG + fanSpan * ratio;
                const angleRad = (angleDeg * Math.PI) / 180;

                const x = Math.cos(angleRad) * radius;
                const y = Math.sin(angleRad) * radius;

                const openDelay = (ringLocalIndex + ringIndex * 0.5) * 0.045;
                const closeDelay = (ringCount - 1 - ringLocalIndex + ringIndex * 0.4) * 0.032;

                btn.style.setProperty('--rt-x', `${x.toFixed(2)}px`);
                btn.style.setProperty('--rt-y', `${y.toFixed(2)}px`);
                btn.style.setProperty('--rt-open-delay', `${openDelay.toFixed(3)}s`);
                btn.style.setProperty('--rt-close-delay', `${closeDelay.toFixed(3)}s`);
                globalIndex++;
            }
        });
    }

    private updateLayoutMetrics(): void {
        const total = this.items.length;
        const ringCapacities = this.getRingCapacities(total);
        const ringCount = ringCapacities.length;
        const maxRadius = this.BASE_RADIUS + (ringCount - 1) * this.RING_GAP;
        const size = Math.ceil(maxRadius + this.MAIN_BUTTON_SIZE + this.SUB_BUTTON_SIZE + this.CANVAS_PADDING * 2);

        this.wrapper.style.width = `${size}px`;
        this.wrapper.style.height = `${size}px`;
        this.wrapper.style.setProperty('--rt-main-size', `${this.MAIN_BUTTON_SIZE}px`);
        this.wrapper.style.setProperty('--rt-sub-size', `${this.SUB_BUTTON_SIZE}px`);
        this.wrapper.style.setProperty('--rt-main-offset', `${(this.MAIN_BUTTON_SIZE - this.SUB_BUTTON_SIZE) / 2}px`);
    }

    public expand(): void {
        if (this.isActive || this.items.length === 0) {
            return;
        }
        this.isActive = true;
        this.wrapper.classList.add('is-active');
    }

    public collapse(): void {
        if (!this.isActive) {
            return;
        }
        this.isActive = false;
        this.wrapper.classList.remove('is-active');
    }

    private updateLocales(): void {
        const mainLabel = t(this.mainButtonLabel);
        this.mainButton.title = mainLabel;
        this.mainButton.setAttribute('aria-label', mainLabel);
        this.itemElements.forEach((el, index) => {
            const item = this.items[index];
            if (!item) {
                return;
            }
            const text = item.title ?? t(item.label);
            el.title = text;
            el.setAttribute('aria-label', text);
            this.applyItemActiveClass(el, item);

            if (!item.icon) {
                const iconEl = el.querySelector('.radial-sub-btn-icon');
                if (iconEl) {
                    iconEl.textContent = this.getFallbackLabel(item.title ?? item.label);
                }
            }
        });
    }

    private applyItemActiveClass(button: HTMLButtonElement, item: RadialMenuItem): void {
        if (!item.isToggle) {
            button.classList.remove('is-active');
            button.dataset.active = 'false';
            return;
        }

        const active = this.toggleStateMap.get(item.id) ?? Boolean(item.isActive);
        button.classList.toggle('is-active', active);
        button.dataset.active = active ? 'true' : 'false';
    }

    private getFallbackLabel(label: string): string {
        const translated = t(label).trim();
        if (!translated) {
            return '?';
        }
        return translated.charAt(0).toUpperCase();
    }

    public setTheme(theme: ThemeConfig): void {
        this.wrapper.classList.remove('theme-light', 'theme-dark');
        this.wrapper.classList.add(`theme-${theme.name}`);

        const style = this.wrapper.style;
        style.setProperty('--bim-primary', theme.primary);
        style.setProperty('--bim-primary-hover', theme.primaryHover);
        style.setProperty('--bim-primary-active', theme.primaryActive);
        style.setProperty('--bim-text-inverse', theme.textInverse);
        style.setProperty('--bim-icon-inverse', theme.iconInverse);
        style.setProperty('--bim-shadow-glow', theme.shadowGlow);

        style.setProperty('--rt-main-bg', theme.bgBase);
        style.setProperty('--rt-main-bg-hover', theme.bgBase);
        style.setProperty('--rt-main-border', theme.floatingBtnBorder);
        style.setProperty('--rt-main-shadow', theme.floatingBtnShadow);
        style.setProperty('--rt-main-shadow-hover', theme.floatingBtnShadowHover);
        style.setProperty('--rt-main-icon', theme.floatingIconColor);
        style.setProperty('--rt-main-icon-hover', theme.floatingIconColorHover);

        style.setProperty('--rt-sub-bg', theme.bgBase);
        style.setProperty('--rt-sub-bg-hover', theme.primaryHover);
        style.setProperty('--rt-sub-border', theme.floatingBtnBorder);
        style.setProperty('--rt-sub-shadow', theme.floatingBtnShadow);
        style.setProperty('--rt-sub-shadow-hover', theme.floatingBtnShadowHover);
        style.setProperty('--rt-sub-icon', theme.floatingIconColor);
        style.setProperty('--rt-sub-icon-hover', theme.iconHover);
    }

    public setItemActive(id: string, active: boolean): void {
        const item = this.items.find((entry) => entry.id === id);
        if (!item || !item.isToggle) {
            return;
        }

        item.isActive = active;
        this.toggleStateMap.set(id, active);
        const button = this.itemButtonMap.get(id);
        if (button) {
            this.applyItemActiveClass(button, item);
        }
    }

    public addItem(item: RadialMenuItem): void {
        this.items.push(item);
        this.renderItems();
        this.updateLayoutMetrics();
    }

    public init(): void { }

    public setLocales(): void {
        this.updateLocales();
    }

    public destroy(): void {
        if (this.unsubscribeTheme) {
            this.unsubscribeTheme();
            this.unsubscribeTheme = null;
        }
        if (this.unsubscribeLocale) {
            this.unsubscribeLocale();
            this.unsubscribeLocale = null;
        }

        this.mainButton.removeEventListener('click', this.handleMainButtonClick);
        this.mainButton.removeEventListener('dblclick', this.handleMainButtonDoubleClick);

        if (this.wrapper.parentNode) {
            this.wrapper.parentNode.removeChild(this.wrapper);
        }
    }
}
